

interface BackupExportSectionProps {
  onExport: () => void;
  exporting?: boolean;
  readOnly?: boolean;
}

export default function BackupExportSection({
  onExport,
  exporting = false,
  readOnly = false,
}: BackupExportSectionProps) {
  return (
    <div className="bg-white rounded-xl border border-stone-100 shadow-sm p-4">
      <h2 className="text-base font-bold text-stone-800 mb-4 font-serif">Backup & Export</h2>
      
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-stone-800 mb-1">Export All Data</h3>
              <p className="text-sm text-stone-600 mb-3">
                Download a complete backup of all your data including venues, vendors, services, packages, clients, events, and more. 
                The export will be in JSON format and can be used for migration or restoration.
              </p>
              <div className="flex items-center gap-4">
                <button
                  onClick={onExport}
                  disabled={exporting || readOnly}
                  className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {exporting ? "Exporting..." : readOnly ? "View Only" : "Download Backup"}
                </button>
                <span className="text-xs text-stone-500">
                  Last backup: Never
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-stone-800 mb-1">Backup Information</h3>
              <div className="text-sm text-stone-600 space-y-1">
                <p><strong>Format:</strong> JSON file with all application data</p>
                <p><strong>Includes:</strong> All tables and relationships</p>
                <p><strong>Size:</strong> Typically 1-10MB depending on data volume</p>
                <p><strong>Frequency:</strong> Recommended weekly for active systems</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-stone-50 border border-stone-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-stone-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg className="w-4 h-4 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-stone-800 mb-1">Important Notes</h3>
              <div className="text-sm text-stone-600 space-y-1">
                <p>• Backup files contain sensitive business data - store securely</p>
                <p>• Keep multiple backup copies in different locations</p>
                <p>• Test restore process before relying on backups</p>
                <p>• Consider automated cloud backup for critical operations</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
